import React, { useEffect, useMemo, useState } from 'react';
import Modal from './ui/Modal';
import { useTranslation } from '../hooks/useTranslation';
import { ICONS } from '../constants';
import {
  ExportShotClip,
  defaultSingleFilename,
  downloadShotFilesSequentially,
  downloadShotFilesToDirectory,
  ensureShotClips,
  requestSingleFileExport,
  supportsDirectoryPicker
} from '../utils/videoExport';

export type ExportVideoMode = 'filePerShot' | 'singleFile';

interface ExportBidVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  biddingSheetId: string;
  projectName?: string;
  biddingName: string;
  bidVersion?: number;
  shots: ExportShotClip[];
  onShotsRefreshed?: (shots: ExportShotClip[]) => void;
}

const ExportBidVideoModal: React.FC<ExportBidVideoModalProps> = ({
  isOpen,
  onClose,
  biddingSheetId,
  projectName,
  biddingName,
  bidVersion = 0,
  shots,
  onShotsRefreshed
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<ExportVideoMode>('filePerShot');
  const [filename, setFilename] = useState(() =>
    defaultSingleFilename(projectName, biddingName)
  );
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; label?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFilename(defaultSingleFilename(projectName, biddingName));
      setError(null);
      setWarnings([]);
      setSuccessMsg(null);
      setProgress(null);
    }
  }, [isOpen, projectName, biddingName]);

  const hasDirectoryPicker = useMemo(() => supportsDirectoryPicker(), []);
  const clipCount = shots.filter((s) => s.videoUrl).length;
  const missingCount = shots.length - clipCount;

  const handleClose = () => {
    if (isExporting) return;
    setError(null);
    setWarnings([]);
    setSuccessMsg(null);
    setProgress(null);
    onClose();
  };

  const runExport = async () => {
    setError(null);
    setWarnings([]);
    setSuccessMsg(null);
    setIsExporting(true);
    setProgress({ done: 0, total: shots.length, label: t('bidding.exportVideoPreparing') });

    try {
      const token = localStorage.getItem('spellbook_token') || '';
      if (!token) {
        throw new Error(t('bidding.exportVideoAuthError'));
      }

      let workingShots = await ensureShotClips(biddingSheetId, shots, token);
      onShotsRefreshed?.(workingShots);

      const withClips = workingShots.filter((s) => s.videoUrl);
      if (withClips.length === 0) {
        throw new Error(t('bidding.exportVideoNoClips'));
      }

      const skippedNames = workingShots
        .filter((s) => !s.videoUrl)
        .map((s) => s.shotName || `shot ${s.shotNumber}`);

      if (mode === 'singleFile') {
        setProgress({ done: 0, total: 1, label: t('bidding.exportVideoConcat') });
        const name = filename.trim() || defaultSingleFilename(projectName, biddingName);
        await requestSingleFileExport(biddingSheetId, name, token);
        setProgress({ done: 1, total: 1 });
        setSuccessMsg(t('bidding.exportVideoSuccessSingle'));
      } else if (hasDirectoryPicker) {
        const dirHandle = await (window as any).showDirectoryPicker({ mode: 'readwrite' });
        const { written, skipped } = await downloadShotFilesToDirectory(
          workingShots,
          dirHandle,
          bidVersion,
          (done, total, label) => setProgress({ done, total, label })
        );
        const allSkipped = [...new Set([...skippedNames, ...skipped])];
        setWarnings(allSkipped);
        setSuccessMsg(
          t('bidding.exportVideoSuccessPerShot').replace('{{count}}', String(written))
        );
      } else {
        const { written, skipped } = await downloadShotFilesSequentially(
          workingShots,
          bidVersion,
          (done, total, label) => setProgress({ done, total, label })
        );
        const allSkipped = [...new Set([...skippedNames, ...skipped])];
        setWarnings(allSkipped);
        setSuccessMsg(
          t('bidding.exportVideoSuccessSequential').replace('{{count}}', String(written))
        );
      }
    } catch (e: any) {
      if (e?.name === 'AbortError') {
        setError(t('bidding.exportVideoCancelled'));
      } else {
        setError(e?.message || t('bidding.exportVideoFailed'));
      }
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('bidding.exportVideoTitle')}
      size="md"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-400">
          {t('bidding.exportVideoIntro')
            .replace('{{total}}', String(shots.length))
            .replace('{{clips}}', String(clipCount))}
          {missingCount > 0 && (
            <span className="text-amber-400 ml-1">
              ({missingCount} {t('bidding.exportVideoMissingClips')})
            </span>
          )}
        </p>

        {/* Mode toggle */}
        <div className="flex rounded-lg overflow-hidden border border-gray-700">
          <button
            type="button"
            disabled={isExporting}
            onClick={() => setMode('filePerShot')}
            className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
              mode === 'filePerShot'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            {t('bidding.exportVideoModePerShot')}
          </button>
          <button
            type="button"
            disabled={isExporting}
            onClick={() => setMode('singleFile')}
            className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
              mode === 'singleFile'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            {t('bidding.exportVideoModeSingle')}
          </button>
        </div>

        {mode === 'filePerShot' && (
          <p className="text-xs text-gray-500">
            {hasDirectoryPicker
              ? t('bidding.exportVideoFolderHint')
              : t('bidding.exportVideoZipHint')}
          </p>
        )}

        {mode === 'singleFile' && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('bidding.exportVideoFilename')}
            </label>
            <input
              type="text"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              disabled={isExporting}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">{t('bidding.exportVideoFilenameHint')}</p>
          </div>
        )}

        {progress && isExporting && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-400">
              <span className="truncate mr-2">{progress.label || t('common.processing')}</span>
              <span>
                {progress.done}/{progress.total}
              </span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-2">
              <div
                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%`
                }}
              />
            </div>
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        {successMsg && (
          <div className="p-3 bg-green-500/10 border border-green-500/50 rounded-lg text-green-400 text-sm">
            {successMsg}
          </div>
        )}

        {warnings.length > 0 && (
          <div className="p-3 bg-amber-500/10 border border-amber-500/50 rounded-lg text-amber-400 text-sm">
            {t('bidding.exportVideoSkipped')}: {warnings.slice(0, 8).join(', ')}
            {warnings.length > 8 ? ` (+${warnings.length - 8})` : ''}
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={isExporting}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {t('common.close')}
          </button>
          <button
            type="button"
            onClick={runExport}
            disabled={isExporting || shots.length === 0}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                <span>{t('common.processing')}</span>
              </>
            ) : (
              <>
                <ICONS.Download className="w-4 h-4" />
                <span>
                  {mode === 'filePerShot'
                    ? hasDirectoryPicker
                      ? t('bidding.exportVideoChooseFolder')
                      : t('bidding.exportVideoDownloadFiles')
                    : t('bidding.exportVideoDownload')}
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ExportBidVideoModal;
